import {useTranslation} from '@aboutyou/i18n';
import {useHistory, useQueryParams} from '@aboutyou/router';
import {useTrackWishlist} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {
  useAddProductToWishlistMutation,
  useFullSession,
  useGetProductIsInWishlist,
  useWishlistCount,
} from '@aboutyou/user';
import {useCallback, useEffect, useRef} from 'react';
import {useSizesSection} from '../../ArticleDetail/useGetProductBulkSection';
import {useGetBadgeListKeys} from '../../ArticleDetail/utils/useGetBadgeListKeys';
import {useSubmitAddToWishlistMission} from '../../Coins/utils/useSubmitAddToWishlistMission';

export const AUTO_ADD_TO_WISHLIST_PARAM = 'a2w';

export const useAutoAddToWishlist = () => {
  const queryParams = useQueryParams();
  const productIdParam = queryParams.get(AUTO_ADD_TO_WISHLIST_PARAM);
  const history = useHistory();
  const getProductIsInWishlist = useGetProductIsInWishlist();
  const showToast = useShowToast();
  const fullSession = useFullSession();
  const wishlistCount = useWishlistCount();
  const addedToWishlistRef = useRef(false);
  const {t} = useTranslation();
  const sizesSection = useSizesSection({productId: Number(productIdParam)});
  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog: false,
    productId: Number(productIdParam),
  });

  const trackWishlist = useTrackWishlist({
    streamPosition: 0,
    product: (sizesSection.data || {}).product!,
    price: sizesSection.data?.price,
    brand: (sizesSection.data || {}).brand!,
    badges: badgesListKeys,
  });

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback
  const removeQueryParam = useCallback(() => {
    queryParams.delete(AUTO_ADD_TO_WISHLIST_PARAM);

    history.replace({
      pathname: history.location.pathname,
      search: queryParams.toString(),
    });
  }, [history, queryParams]);

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback
  const onError = useCallback(
    (err?: string) => {
      showToast({
        content: err || t('Oops! Something went wrong!'),
        type: 'ERROR',
      });

      removeQueryParam();
    },
    [showToast, t, removeQueryParam],
  );

  const onAddToWishlistSuccess = useSubmitAddToWishlistMission();

  const addProductToWishlist =
    useAddProductToWishlistMutation(removeQueryParam);

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback
  const autoAddToWishlist = useCallback(
    async (productId: number | null) => {
      if (
        productId === null ||
        !fullSession ||
        wishlistCount.isLoading ||
        addedToWishlistRef.current
      ) {
        return;
      }

      if (getProductIsInWishlist({productId})) {
        showToast({
          content: t('This product is already in your wishlist.'),
          type: 'WARNING',
        });

        removeQueryParam();
        return;
      }

      try {
        addedToWishlistRef.current = true;

        await addProductToWishlist.mutateAsync({
          productId,
          onError,
          onSuccess: onAddToWishlistSuccess,
        });

        trackWishlist('add-to-wishlist');
      } catch (e) {
        onError();
      }
    },
    [
      removeQueryParam,
      onError,
      showToast,
      fullSession,
      wishlistCount.isLoading,
      getProductIsInWishlist,
      addProductToWishlist,
      trackWishlist,
      t,
      onAddToWishlistSuccess,
    ],
  );

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback
  useEffect(() => {
    if (productIdParam) {
      autoAddToWishlist(productIdParam ? Number(productIdParam) : null);
    }
  }, [autoAddToWishlist, productIdParam]);

  return {isAutoAddToWishlistLoading: Boolean(productIdParam)};
};
